/// <amd-module name="VSS/Features/Frame/ResponsiveLayout/ResponsiveLayout" />
import React = require("react");
import ReactDOM = require("react-dom");
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IIndexedAdjustment, IResponsiveLayoutProps, ResponsiveOrientation } from "./ResponsiveLayout.Props";

/**
 * Details about a child component that were computed during the adjustment computations.
 */
interface IAdjustDetail {
    /**
     * adjustmentIndex of the component based on the available space. If no value exists the
     * component is not adjusted. When the component is rendered it may have a value 0 or
     * greater depending on whether or not any non-hidden adjustments were specified.
     *
     * -1 means no adjustments have been applied.
     */
    adjustmentIndex: number;

    /**
     * Width of the component during the computation of adjustment phase.
     */
    clientRect: ClientRect;

    /**
     * If the component has been hidden, it will no longer be rendered into the parent.
     */
    hidden?: boolean;
}

export class ResponsiveLayout extends VssComponent<IResponsiveLayoutProps, {}> {
    /**
     * Adjustment related fields, used in managing components that auto adjust their contents.
     */
    private appliedAdjustments: number[];
    private adjustDetails?: IAdjustDetail[];
    private adjustedIndex: number = 0;
    private adjustments: IIndexedAdjustment[];

    constructor(props: IResponsiveLayoutProps) {
        super(props);

        this.addEventListener(window, "resize", () => {
            // @TODO: Put in a throttle here.
            this.updateLayout();
        });
    }

    public render(): JSX.Element | null {
        let container = React.Children.only(this.props.children) as React.DetailedReactHTMLElement<any, HTMLElement>;
        return React.cloneElement(
            container,
            container.props,
            React.Children.map(container.props.children, (child: React.ReactChild, index: number) => {
                if (this.adjustDetails && this.adjustDetails[index]) {
                    // If the component has been hidden by an adjustment, we will render a placeholder, that takes 0 size.
                    if (this.adjustDetails[index].hidden) {
                        return <div key={"PH" + index} className="responsive-placeholder" />;
                    }

                    // If an intermediate adjustment has been applied we will update the components properties with the index.
                    else if (this.adjustDetails[index].adjustmentIndex >= 0) {
                        const childElement = child as React.ReactElement<IVssComponentProperties>;
                        const childElementProps: IVssComponentProperties = Object.assign({}, childElement.props, {
                            adjustmentIndex: this.adjustDetails[index].adjustmentIndex
                        });
                        return React.cloneElement(childElement, childElementProps, childElement.props && childElement.props.children);
                    }
                }

                return child;
            })
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.updateLayout();
    }

    public componentDidUpdate(previousProps: IResponsiveLayoutProps, previousState: {}): void {
        super.componentDidUpdate(previousProps, previousState);
        this.updateLayout();
    }

    public resetLayout(): void {
        delete this.adjustDetails;
        this.adjustedIndex = 0;
    }

    private updateLayout = () => {
        const componentElement = ReactDOM.findDOMNode(this) as Element;
        if (componentElement && componentElement.children.length) {
            const initialAdjustedIndex = this.adjustedIndex;
            const componentClientRect = componentElement.getBoundingClientRect();
            let renderedSize: number = 0;
            let ignoredSize: number = 0;
            let initialPass: boolean = false;

            // If this is the first time we have seen this set of items, we need to get measurements
            // with no adjustments to understand the native size of these components.
            if (!this.adjustDetails || componentElement.children.length !== this.adjustDetails.length) {
                this.appliedAdjustments = [];
                this.adjustDetails = [];

                // Now sort the adjustments by the priority then index.
                this.adjustments = this.props.adjustments.slice(0, this.props.adjustments.length);
                this.adjustments.sort((a1, a2) => {
                    let result = (a1.priority || Number.MAX_VALUE) - (a2.priority || Number.MAX_VALUE);
                    if (result === 0) {
                        result = a2.index - a1.index;
                    }

                    return result;
                });

                initialPass = true;
            }

            // We need to go through and compute the sizes of the child components.
            for (let componentIndex = 0; componentIndex < componentElement.children.length; componentIndex++) {
                const clientRect = componentElement.children[componentIndex].getBoundingClientRect();

                // If this is the initial pass we will create new adjust entries for this components
                // otherwise we will just update the current state.
                if (initialPass) {
                    this.adjustDetails.push({ adjustmentIndex: -1, clientRect: clientRect });
                } else {
                    this.adjustDetails[componentIndex].clientRect = clientRect;
                }

                // Track ignored component sizes independently, this will help with rounding issues.
                if (this.props.ignoredChildren[componentIndex]) {
                    ignoredSize += this.props.orientation === ResponsiveOrientation.Vertical ? clientRect.height : clientRect.width;
                }
            }

            // The renderedSize is equal to the size from the end of the last component to the
            // start of the first component minus any ignored space.
            if (this.props.orientation === ResponsiveOrientation.Vertical) {
                renderedSize = this.adjustDetails[this.adjustDetails.length - 1].clientRect.bottom - this.adjustDetails[0].clientRect.top;
            } else {
                renderedSize = this.adjustDetails[this.adjustDetails.length - 1].clientRect.right - this.adjustDetails[0].clientRect.left;
            }

            renderedSize -= Math.floor(ignoredSize);

            // If there is not enough space we will try to adjust items smaller first.
            const componentClientSize =
                this.props.orientation === ResponsiveOrientation.Vertical ? componentClientRect.height : componentClientRect.width;
            if (componentClientSize <= renderedSize) {
                const previouslyApplied: { [adjustIndex: number]: number } = {};
                let availableSpace = componentClientSize - renderedSize;

                while (availableSpace < 0 && this.adjustedIndex < this.adjustments.length) {
                    const adjustment = this.adjustments[this.adjustedIndex];
                    const adjustDetail = this.adjustDetails[adjustment.index];

                    // Determine who much space we will recoupe from this component adjustment.
                    let appliedSize;

                    if (this.props.orientation === ResponsiveOrientation.Vertical) {
                        appliedSize = adjustDetail.clientRect!.height - (previouslyApplied[adjustment.index] || 0);
                    } else {
                        appliedSize = adjustDetail.clientRect!.width - (previouslyApplied[adjustment.index] || 0);
                    }

                    // If the component has multiple adjustments we need to apply the next one in the list
                    // otherwise we will apply all the space and hide the component.
                    if (adjustment.approxSize) {
                        appliedSize -= adjustment.approxSize!;
                    } else {
                        adjustDetail.hidden = true;
                    }

                    // Apply the next adjustment to the child components.
                    adjustDetail.adjustmentIndex++;
                    availableSpace += appliedSize;

                    // Track how space we recouped from applying this adjustment.
                    this.appliedAdjustments[this.adjustedIndex] = appliedSize;

                    // Save the amount we have already applied in this phase so we dont apply it again
                    // if we adjust the component further.
                    previouslyApplied[adjustment.index] = appliedSize + (previouslyApplied[adjustment.index] || 0);
                    this.adjustedIndex++;

                    // console.log("Adjust (shrink), applied " + appliedSize + " from adjustIndex " + this.adjustedIndex + " component " + adjustment.index + "/" + adjustDetail.adjustmentIndex);
                }
            }

            // If we have availableSpace and there are adjusted items we should see if we can give
            // some back to items.
            else if (componentClientSize > renderedSize) {
                let availableSpace = componentClientSize - renderedSize;

                while (this.adjustedIndex > 0 && this.appliedAdjustments[this.adjustedIndex - 1]! < availableSpace) {
                    const adjustment = this.adjustments[--this.adjustedIndex];
                    const adjustDetail = this.adjustDetails[adjustment.index];

                    // Apply the next adjustment to the child components.
                    adjustDetail.adjustmentIndex--;
                    availableSpace -= this.appliedAdjustments[this.adjustedIndex];
                    adjustDetail.hidden = false;

                    // console.log("Adjust (grow), applied " + this.appliedAdjustments[this.adjustedIndex] + " from adjustIndex " + this.adjustedIndex + " component " + adjustment.index + "/" + adjustDetail.adjustmentIndex);
                }
            }

            // If adjustments were applied we need to notify the owner on the change and re-layout.
            if (initialAdjustedIndex != this.adjustedIndex) {
                if (this.props.onLayoutChange) {
                    this.props.onLayoutChange(this.adjustedIndex - 1);
                }

                // Now render the updated components and we will see if we have gained enough space.
                window.setTimeout(() => {
                    this.forceUpdate();
                }, 0);
            }
        }
    };
}
